"use client";

import { Layout, LayoutProps } from "skedyul-ui/ui";
import {
  transformAppStateToLayoutContent,
  getDialogContent,
} from "./_schema.layout";
import { usePathname, useRouter } from "next/navigation";
import { useParams, useSearchParams } from "next/navigation";
import { useSelector } from "react-redux";
import { type RootState } from "@store/index";
import { useTheme } from "@teispace/next-themes";
import { useEffect, useCallback, useState } from "react";
import { store } from "@store/index";
import { DialogSchemaProps } from "@schemas/dialog.schema";
import { getWorkflowRun } from "@store/features/settings/workflowSlice";
import { useBreakpoint } from "skedyul-ui/ui";
import { open as openSideNavigation } from "@store/features/layout/sideNavigationSlice";
import { WorkflowsProvider, useStreamWorkflowRun } from "@providers/workflows";
import {
  DeploymentsProvider,
  useStreamDeploymentLogs,
} from "@providers/deployments";
import {
  ProvisionsProvider,
  useStreamProvisionUpdates,
} from "@providers/provisions";
import WorkplaceProvider from "@providers/workplace";
import { AgentChatProvider } from "@providers/agentChatProvider";
import { useSyncThreadSidebarFilter } from "@hooks/useSyncThreadSidebarFilter";
import { useFetchThreadsOnSidebarOpen } from "@hooks/useFetchThreadsOnSidebarOpen";
import { useNewThreadShortcut } from "@hooks/useNewThreadShortcut";

interface Props {
  children: React.ReactNode;
}

const openWorkflowSidebar = async (workflowRunId: string) => {
  await store.dispatch(getWorkflowRun({ workflowRunId }));
  store.dispatch(
    openSideNavigation({
      position: "right",
      schema: "WORKFLOW_RUN",
      props: { width: "600px", mobileWidth: "80vw" },
    }),
  );
};

const openDeploymentSidebar = (deploymentId: string) => {
  store.dispatch(
    openSideNavigation({
      position: "right",
      schema: "DEPLOYMENT_LOGS",
      params: { deploymentId },
      props: {
        width: "750px",
        mobileWidth: "90vw",
      },
    }),
  );
};

const openProvisionSidebar = (provisionId: string) => {
  store.dispatch(
    openSideNavigation({
      position: "right",
      schema: "PROVISION_LOGS",
      params: { provisionId },
      props: {
        width: "600px",
        mobileWidth: "90vw",
      },
    }),
  );
};

const LayoutShell: React.FC<Props> = ({ children }) => {
  const pathname = usePathname();
  const params = useParams<{ subdomain: string }>();
  const searchParams = useSearchParams();
  const router = useRouter();
  const theme = useTheme();

  useSyncThreadSidebarFilter();
  useFetchThreadsOnSidebarOpen();
  useNewThreadShortcut();

  const { isBelowMd } = useBreakpoint("md");
  const [dialogContent, setDialogContent] = useState<DialogSchemaProps>({
    sections: [],
    title: "",
    description: "",
    actions: [],
  });

  const appState = useSelector(
    (rootState: RootState) => ({
      workplace: rootState.workplace,
      sideNavigation: rootState.sideNavigation,
      dialog: rootState.dialog,
      workflows: rootState.workflows,
      deployments: rootState.deployments,
      provisions: rootState.provisions,
      agents: rootState.agents,
      threads: rootState.threads,
      crmField: rootState.crm.field,
      crmModel: rootState.crm.model,
      crmRelationship: rootState.crm.relationship,
      crmNormalizations: rootState.crm.normalizations,
    }),
    (prev, next) => {
      return (
        prev.workplace === next.workplace &&
        prev.sideNavigation === next.sideNavigation &&
        prev.dialog === next.dialog &&
        prev.workflows === next.workflows &&
        prev.deployments === next.deployments &&
        prev.provisions === next.provisions &&
        prev.agents === next.agents &&
        prev.threads === next.threads &&
        prev.crmField === next.crmField &&
        prev.crmModel === next.crmModel &&
        prev.crmRelationship === next.crmRelationship &&
        prev.crmNormalizations === next.crmNormalizations
      );
    },
  );

  const content = transformAppStateToLayoutContent(
    appState,
    store.getState(),
    isBelowMd,
    theme,
    undefined as unknown as never,
    {
      pathname,
      params,
      router,
    },
  );

  const loadDialogContent = useCallback(async () => {
    if (appState.dialog.open && appState.dialog.schema) {
      const dynamicContent = await getDialogContent(
        appState,
        store.getState(),
        appState.dialog,
        undefined as unknown as never,
        router,
      );
      setDialogContent(dynamicContent);
    }
  }, [appState, router]);

  useEffect(() => {
    void loadDialogContent();
  }, [loadDialogContent]);

  const mergedContent: LayoutProps = {
    ...content,
    dialog: content.dialog
      ? {
          ...content.dialog,
          ...dialogContent,
        }
      : undefined,
  };

  const workflowRunId = searchParams.get("run");
  const deploymentId = searchParams.get("deployment");
  const provisionId = searchParams.get("provision");

  useEffect(() => {
    if (workflowRunId) {
      void openWorkflowSidebar(workflowRunId);
    }
  }, [workflowRunId]);

  useEffect(() => {
    if (deploymentId) {
      openDeploymentSidebar(deploymentId);
    }
  }, [deploymentId]);

  useEffect(() => {
    if (provisionId) {
      openProvisionSidebar(provisionId);
    }
  }, [provisionId]);

  const deploymentSidebarId =
    deploymentId ||
    (appState.sideNavigation.right?.schema === "DEPLOYMENT_LOGS"
      ? (appState.sideNavigation.right?.params?.deploymentId as
          | string
          | undefined)
      : undefined);

  useStreamDeploymentLogs(deploymentSidebarId);

  const provisionSidebarId =
    provisionId ||
    (appState.sideNavigation.right?.schema === "PROVISION_LOGS"
      ? (appState.sideNavigation.right?.params?.provisionId as
          | string
          | undefined)
      : undefined);

  useStreamProvisionUpdates(provisionSidebarId);

  const callUseStreamWorkflowRun: (
    id?: string | null,
    cb?: (data: unknown) => void,
  ) => void = useStreamWorkflowRun as unknown as (
    id?: string | null,
    cb?: (data: unknown) => void,
  ) => void;

  callUseStreamWorkflowRun(workflowRunId, () => {
    if (workflowRunId) {
      void store.dispatch(getWorkflowRun({ workflowRunId }));
    }
  });

  const isReportGeneratePage = pathname?.match(/\/reports\/[^/]+\/generate/);
  // In-page list/detail routes keep the layout mounted across child navigations.
  // Using key={pathname} elsewhere remounts the whole page (feels like a full refresh).
  const isPersistentChildNavRoute =
    /\/threads(\/|$)/.test(pathname ?? "") ||
    /\/developers\/playground(\/|$)/.test(pathname ?? "") ||
    /\/developers\/agents\/[^/]+\/improve(\/|$)/.test(pathname ?? "");

  if (isReportGeneratePage) {
    return <div className="min-h-screen bg-background">{children}</div>;
  }

  return (
    <Layout {...mergedContent} lockPageScroll={isPersistentChildNavRoute}>
      {isPersistentChildNavRoute ? (
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          {children}
        </div>
      ) : (
        <div
          key={pathname}
          className="h-full min-h-full animate-nav-content-in"
        >
          {children}
        </div>
      )}
    </Layout>
  );
};

const RootLayout: React.FC<Props> = ({ children }) => {
  return (
    <WorkplaceProvider>
      <WorkflowsProvider>
        <DeploymentsProvider>
          <ProvisionsProvider>
            <AgentChatProvider>
              <LayoutShell>{children}</LayoutShell>
            </AgentChatProvider>
          </ProvisionsProvider>
        </DeploymentsProvider>
      </WorkflowsProvider>
    </WorkplaceProvider>
  );
};

export default RootLayout;
